import React, { useEffect, useMemo, useState, useCallback, useContext } from 'react';
import useModuleHotKeys from '../../hooks/useModuleHotKeys';
import { systemIsMac } from '@web-mail/util';
import { MAIL_HOTKEY_MODULE_TYPE } from '@web-mail/common/constant';
import { useAppSelector } from '@web-common/state/createStore';
import { formatHotKey2Lib } from '@web-common/utils/hotkey';
import debounce from 'lodash/debounce';
import { apiHolder as api, } from 'api';
import useStateRef from '@web-mail/hooks/useStateRef';
const eventApi = api.api.getEventApi();
const isMac = systemIsMac();
export const MailModuleHKContent = React.createContext({});
export const ModuleHotKeyEvent = props => {
    const { handlers, keyMap, hkDisabled } = props;
    const { setMailGlobalHk, clearHk } = useContext(MailModuleHKContent);
    const keyMapRef = useStateRef(keyMap);
    const hkDisabledRef = useStateRef(hkDisabled);
    useEffect(() => {
        if (hkDisabled) {
            clearHk && clearHk(MAIL_HOTKEY_MODULE_TYPE.MAIL_LIST);
        }
        else {
            if (handlers) {
                const keymap = keyMapRef.current;
                setMailGlobalHk && setMailGlobalHk(MAIL_HOTKEY_MODULE_TYPE.MAIL_LIST, handlers, keymap);
            }
        }
    }, [hkDisabled]);
    useEffect(() => {
        if (handlers && !hkDisabledRef.current) {
            const keymap = keyMapRef.current;
            setMailGlobalHk && setMailGlobalHk(MAIL_HOTKEY_MODULE_TYPE.MAIL_LIST, handlers, keymap);
        }
        return () => {
            clearHk && clearHk(MAIL_HOTKEY_MODULE_TYPE.MAIL_LIST);
        };
    }, [handlers]);
    return React.createElement("div", null, props.children);
};
export const ModuleHotKey = props => {
    const { name = 'mailbox' } = props;
    const HotKeys = useModuleHotKeys(name);
    const globalHotKeysMap = useAppSelector(state => state.hotKeyReducer.globalHotKeysMap);
    const mailHotKeysMap = useAppSelector(state => state.hotKeyReducer.mailHotKeysMap);
    const [HotKeyHandlerMap, setHotKeyHandler] = useState({});
    const [HotKeyKeyMap, setHotKeyKeyMap] = useState({});
    const handleSetHk = useCallback((name, handler, keyMap) => {
        if (name != null) {
            if (keyMap) {
                setHotKeyKeyMap(map => {
                    return Object.assign(Object.assign({}, map), { [name]: keyMap || {} });
                });
            }
            setHotKeyHandler(map => {
                return Object.assign(Object.assign({}, map), { [name]: handler });
            });
        }
    }, []);
    const handleClearHk = useCallback((name) => {
        if (name != null) {
            setHotKeyHandler(map => {
                return Object.assign(Object.assign({}, map), { [name]: null });
            });
            setHotKeyKeyMap(map => {
                return Object.assign(Object.assign({}, map), { [name]: null });
            });
        }
    }, []);
    const mergeHandler = useMemo(() => {
        const map = {};
        const levelMap = {};
        for (let moduleKey in HotKeyHandlerMap) {
            const moduleName = moduleKey;
            const hotKeyHandler = HotKeyHandlerMap[moduleName] || {};
            if (hotKeyHandler) {
                for (let n in hotKeyHandler) {
                    const data = hotKeyHandler[n];
                    if (typeof data === 'function') {
                        map[n] = hotKeyHandler[n];
                        continue;
                    }
                    else if (typeof data === 'object') {
                        const { level, handler } = data;
                        if (map[n]) {
                            if (level != null && levelMap[n] && levelMap[n] < level) {
                                map[n] = handler;
                                levelMap[n] = level || 0;
                            }
                        }
                        else {
                            map[n] = handler;
                            if (level != null) {
                                levelMap[n] = level || 0;
                            }
                        }
                        continue;
                    }
                }
            }
        }
        return Object.assign({}, map);
    }, [HotKeyHandlerMap, HotKeyKeyMap]);
    const mergedKeyMap = useMemo(() => {
        const consolidatedMap = {};
        for (let moduleKey in HotKeyHandlerMap) {
            const moduleName = moduleKey;
            const hotKeyHandler = HotKeyHandlerMap[moduleName];
            const moduleKeyMap = HotKeyKeyMap[moduleName] || {};
            for (let key in moduleKeyMap) {
                if (moduleKeyMap[key]) {
                    consolidatedMap[key] = moduleKeyMap[key];
                }
            }
            if (!HotKeyKeyMap[moduleName] && hotKeyHandler) {
                for (let key in hotKeyHandler) {
                    let _key = key;
                    if (globalHotKeysMap[_key]) {
                        consolidatedMap[key] = formatHotKey2Lib(globalHotKeysMap[_key]);
                    }
                    else if (mailHotKeysMap[_key]) {
                        consolidatedMap[key] = formatHotKey2Lib(mailHotKeysMap[_key]);
                    }
                    else {
                        consolidatedMap[key] = key;
                    }
                }
            }
        }
        return Object.assign({}, consolidatedMap);
    }, [HotKeyHandlerMap, HotKeyKeyMap, globalHotKeysMap, mailHotKeysMap]);
    return (React.createElement(MailModuleHKContent.Provider, { value: {
            setMailGlobalHk: handleSetHk,
            clearHk: handleClearHk,
        } },
        React.createElement(HotKeys, { keyMap: mergedKeyMap, handlers: mergeHandler, allowChanges: true, style: { width: '100%', height: '100%' } }, props.children)));
};
export const useRegistMailHK = (name, params) => {
    const { handler, keyMap, disabled } = params || {};
    const { setMailGlobalHk, clearHk } = useContext(MailModuleHKContent);
    const keyMapRef = useStateRef(keyMap);
    const disabledRef = useStateRef(disabled);
    const localHandler = useMemo(() => {
        if (handler) {
            const res = {};
            for (let i in handler) {
                const data = handler[i];
                if (typeof data === 'function') {
                    res[i] = debounce((e) => {
                        if (e.preventDefault) {
                            e.preventDefault();
                        }
                        data && data(e);
                    }, 500, { leading: true, trailing: false });
                    continue;
                }
                if (typeof data === 'object') {
                    const { handler, debounce: debounceConfig = true, preventDefault = true, level, } = data;
                    if (debounceConfig === false) {
                        if (level != null) {
                            res[i] = {
                                handler,
                                level,
                            };
                        }
                        else {
                            res[i] = handler;
                        }
                    }
                    else {
                        const fn = debounce((e) => {
                            if (preventDefault && e.preventDefault) {
                                e.preventDefault();
                            }
                            handler && handler(e);
                        }, typeof debounceConfig === 'object' && (debounceConfig === null || debounceConfig === void 0 ? void 0 : debounceConfig.wait) != null ? debounceConfig === null || debounceConfig === void 0 ? void 0 : debounceConfig.wait : 500, typeof debounceConfig === 'object' ? debounceConfig : { leading: true, trailing: false });
                        if (level != null) {
                            res[i] = {
                                handler: fn,
                                level,
                            };
                        }
                        else {
                            res[i] = fn;
                        }
                    }
                    continue;
                }
            }
            return res;
        }
        return {};
    }, [handler]);
    useEffect(() => {
        if (disabled) {
            clearHk && clearHk(name);
        }
        else {
            if (localHandler) {
                setMailGlobalHk && setMailGlobalHk(name, localHandler, keyMap);
            }
        }
    }, [disabled]);
    useEffect(() => {
        if (localHandler && !disabledRef.current) {
            setMailGlobalHk && setMailGlobalHk(name, localHandler, keyMapRef.current);
        }
        return () => {
            clearHk && clearHk(name);
        };
    }, [localHandler]);
    return !name
        ? {
            setMailGlobalHk,
            clearHk,
        }
        : null;
};
export default ModuleHotKey;
